import { Controller } from "@hotwired/stimulus";

// share-link
// ──────────
// Handles the "Compartilhar" button on boards and tasks.
// Opens an inline popover, fetches/creates a short link via AJAX,
// and copies it to the clipboard.
//
// Usage (the _share_popover.html.erb partial wires this automatically):
//   data-controller="share-link"
//   data-share-link-shareable-type-value="Board"
//   data-share-link-shareable-id-value="<uuid>"
//   data-share-link-create-url-value="/share_links"

export default class extends Controller {
  static targets = [
    "popover",
    "loading",
    "urlText",
    "urlRow",
    "copyBtn",
    "openBtn",
    "error",
  ];

  static values = {
    shareableType: String,
    shareableId: String,
    createUrl: String,
  };

  connect() {
    this.shortUrl = null;
    this._outsideClick = this._handleOutsideClick.bind(this);
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick);
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  async open(event) {
    event.preventDefault();
    event.stopPropagation();

    // Toggle: close if already open
    if (
      this.hasPopoverTarget &&
      !this.popoverTarget.classList.contains("hidden")
    ) {
      this.close();
      return;
    }

    // If we already fetched the URL, just show the popover immediately
    if (this.shortUrl) {
      this._showPopoverWithUrl(this.shortUrl);
      return;
    }

    this._showLoading();

    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          shareable_type: this.shareableTypeValue,
          shareable_id: this.shareableIdValue,
        }),
        credentials: "same-origin",
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        this._showError(body.error || "Erro ao gerar link. Tente novamente.");
        return;
      }

      const { short_url } = await response.json();
      this.shortUrl = short_url;
      this._showPopoverWithUrl(short_url);
    } catch {
      this._showError("Erro de conexão. Verifique sua internet e tente novamente.");
    }
  }

  async copy(event) {
    event.preventDefault();
    if (!this.shortUrl) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.shortUrl);
      } else {
        this._execCommandCopy(this.shortUrl);
      }
      this._showCopied();
    } catch {
      this._showError("Não foi possível copiar. Tente manualmente.");
    }
  }

  openLink(event) {
    event.preventDefault();
    if (this.shortUrl) {
      window.open(this.shortUrl, "_blank", "noopener,noreferrer");
    }
  }

  close() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add("hidden");
    }
    document.removeEventListener("click", this._outsideClick);
  }

  // ── Private ──────────────────────────────────────────────────────────────

  _showLoading() {
    if (!this.hasPopoverTarget) return;
    this.popoverTarget.classList.remove("hidden");
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden");
    if (this.hasUrlRowTarget) this.urlRowTarget.classList.add("hidden");
    if (this.hasCopyBtnTarget) this.copyBtnTarget.classList.add("hidden");
    if (this.hasOpenBtnTarget) this.openBtnTarget.classList.add("hidden");
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden");
    document.addEventListener("click", this._outsideClick);
  }

  _showPopoverWithUrl(url) {
    if (!this.hasPopoverTarget) return;
    this.popoverTarget.classList.remove("hidden");
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden");
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden");
    if (this.hasUrlTextTarget) this.urlTextTarget.textContent = url;
    if (this.hasUrlRowTarget) this.urlRowTarget.classList.remove("hidden");
    if (this.hasCopyBtnTarget) this.copyBtnTarget.classList.remove("hidden");
    if (this.hasOpenBtnTarget) this.openBtnTarget.classList.remove("hidden");
    document.addEventListener("click", this._outsideClick);
  }

  _showCopied() {
    if (!this.hasCopyBtnTarget) return;
    const btn = this.copyBtnTarget;
    const original = btn.innerHTML;
    btn.innerHTML =
      '<svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>' +
      '<span>Link copiado!</span>';
    btn.classList.add("bg-emerald-600", "hover:bg-emerald-500");
    btn.classList.remove("bg-purple-600", "hover:bg-purple-500");
    setTimeout(() => {
      btn.innerHTML = original;
      btn.classList.remove("bg-emerald-600", "hover:bg-emerald-500");
      btn.classList.add("bg-purple-600", "hover:bg-purple-500");
    }, 2200);
  }

  _showError(message) {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden");
    if (this.hasUrlRowTarget) this.urlRowTarget.classList.add("hidden");
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  _handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  _execCommandCopy(text) {
    const el = document.createElement("textarea");
    el.value = text;
    el.className = "sr-only";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    el.remove();
  }
}
